/**
 * Shared organize-feature types.
 *
 * `GoalType` and its label/option maps used to be duplicated across
 * OrganizeGoalsPage and GoalDetailPage. They now live here so views, cards, and
 * creation flows all import one definition.
 *
 * NOTE: `routine` is still present in the union for now — it is removed as a
 * goal type in a later PR (Workstream D.2), which also handles migrating any
 * existing routine-type goals. Keeping it here avoids a behavior change in this
 * pure-decomposition PR.
 */
// "routine" is NO LONGER a creatable goal type (routines are a separate flow —
// see flow restructure spec §7.2). It remains in the union only so LEGACY
// routine-type goals still type-check and their detail/history stays viewable.
// New goals are created as one of `CreatableGoalType`.
export type GoalType = "find_home" | "reorganize" | "routine" | "declutter";
export type CreatableGoalType = "find_home" | "reorganize" | "declutter";

/** Short labels used for type badges on the goal detail views. */
export const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  find_home: "Find a home",
  reorganize: "Reorganize",
  routine: "Routine",
  declutter: "Declutter",
};

/**
 * Copy for each creatable goal kind — used in the create modal.
 * "Build a routine" is intentionally absent: a routine is repeat maintenance,
 * not one discrete goal, so it lives in the separate Routines flow.
 */
export const GOAL_TYPE_OPTIONS: { value: CreatableGoalType; label: string; hint: string }[] = [
  { value: "find_home", label: "Find a home", hint: "Decide where specific items should live" },
  { value: "reorganize", label: "Reorganize a space", hint: "Rework how a room or container is organized" },
  { value: "declutter", label: "Declutter", hint: "Reduce stuff and let things go" },
];

/** The creatable goal types as a runtime array — used to validate `?type=` deep links. */
export const CREATABLE_GOAL_TYPES: CreatableGoalType[] = GOAL_TYPE_OPTIONS.map((o) => o.value);

// ---------------------------------------------------------------------------
// Goal detail shapes (shared by GoalDetailPage dispatcher + per-type views)
// ---------------------------------------------------------------------------

export interface GoalItem {
  id: string;
  itemId: string;
  itemName: string;
  itemSpaceId: string | null;
  /** Re-locate flag timestamp (P0-B) — null when not flagged. */
  itemRelocateAt?: string | null;
  /** Planned move (decided, not yet moved) — null when none. */
  itemPlannedSpaceId?: string | null;
  itemPlannedSpaceName?: string | null;
  spaceName: string | null;
  createdAt: string;
}

export interface Trial {
  id: string;
  goalId: string | null;
  targetSpaceId: string;
  status: string;
  startedAt: string | null;
  reviewAfter: string | null;
  decidedAt: string | null;
  outcomeNote: string | null;
  createdAt: string;
}

export interface GoalDetail {
  id: string;
  title: string;
  description: string | null;
  activityLocationId: string | null;
  status: string;
  type: GoalType;
  userId: string;
  householdId: string;
  createdAt: string;
  updatedAt: string;
  items: GoalItem[];
  activeTrials: Trial[];
}

export interface InventoryItem {
  id: string;
  name: string;
  spaceId: string | null;
  spaceName?: string | null;
}

export interface DiagnosisResult {
  activityLocation: { id: string; name: string } | null;
  items: { id: string; name: string; currentSpace: { id: string; name: string } | null; inPlace: boolean }[];
  summary: string;
  inPlaceCount: number;
  totalCount: number;
}

export interface PlacementSuggestion {
  suggestedSpaceId: string | null;
  suggestedSpaceName: string | null;
  reasoning: string;
}

export interface TrialHistoryEntry {
  id: string;
  goalId: string | null;
  targetSpaceId: string;
  targetSpaceName: string | null;
  status: string;
  startedAt: string | null;
  reviewAfter: string | null;
  decidedAt: string | null;
  outcomeNote: string | null;
  userId: string;
  createdAt: string;
  frictionFlagCount: number;
}

export interface FrictionFlag {
  id: string;
  trialId: string;
  userId: string;
  note: string | null;
  createdAt: string;
}

export const TRIAL_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  created: { label: "Awaiting move", color: "text-warning-text bg-warning-wash" },
  confirmed_moved: { label: "In progress", color: "text-amethyst-ink bg-amethyst-soft" },
  in_progress: { label: "In progress", color: "text-amethyst-ink bg-amethyst-soft" },
  adopted: { label: "Adopted ✓", color: "text-sage-deep bg-sage-wash" },
  reverted: { label: "Reverted", color: "text-body bg-clay-muted" },
  expired_not_started: { label: "Expired", color: "text-faint bg-clay-muted" },
};
